import * as React from "react";
import { Dialog, VisuallyHidden } from "@radix-ui/themes";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useConvex } from "convex/react";
import { api } from "@repo/convex/convex/_generated/api";
import { toast } from "sonner";
import { SearchResult } from "@repo/convex/convex/search.ts";
import { SearchItem } from "./SearchItem.tsx";
import { useEffect } from "react";
import { useThrottle } from "../common/hooks.ts";
import { routes } from "../routing/router.ts";
import "./search.css";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => unknown;
}

export const SearchDialog: React.FC<Props> = ({ isOpen, setIsOpen }) => {
  const searchRef = React.useRef<HTMLInputElement>(null);
  const [term, setTerm] = React.useState("");
  const [results, setResults] = React.useState<SearchResult[]>([]);
  // The search itself, all in this one place: what's typed, a little throttled, to search:search
  const convex = useConvex();
  const throttledTerm = useThrottle(term);

  useEffect(() => {
    if (!throttledTerm) return;
    convex.query(api.search.search, { term: throttledTerm }).then(setResults).catch(toast.error);
  }, [throttledTerm]);

  const shown = term.trim() ? results : [];
  const [selected, setSelected] = React.useState(0);
  useEffect(() => setSelected(0), [results]);

  const open = (result: SearchResult) => {
    setIsOpen(false);
    routes.item({ itemId: result.item._id }).push();
  };

  // Up and down through the results, Enter to open one, like a command palette
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!shown.length) return;
    if (event.key == "ArrowDown" || event.key == "ArrowUp") {
      event.preventDefault();
      const step = event.key == "ArrowDown" ? 1 : -1;
      setSelected((i) => (i + step + shown.length) % shown.length);
    } else if (event.key == "Enter") {
      event.preventDefault();
      const result = shown[Math.min(selected, shown.length - 1)];
      if (result) open(result);
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(isOpen) => {
        setIsOpen(isOpen);
      }}
    >
      <Dialog.Content
        aria-describedby={undefined}
        maxWidth={"min(calc(100dvw - 24px), 640px)"}
        align={"start"}
        style={{ padding: 0, marginTop: "10vh", overflow: "hidden" }}
        onKeyDown={onKeyDown}
      >
        <VisuallyHidden>
          <Dialog.Title>Search</Dialog.Title>
        </VisuallyHidden>

        <div className="search-field">
          <MagnifyingGlassIcon width="18" height="18" />
          <input
            ref={searchRef}
            placeholder="Search your stash, archive and notes"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            autoFocus={true}
            aria-label="Search"
            role="combobox"
            aria-expanded={shown.length > 0}
            aria-controls="search-results"
          />
          <Dialog.Close>
            <button type="button" className="kbd search-esc" aria-label="Close">
              Esc
            </button>
          </Dialog.Close>
        </div>

        {shown.length > 0 ? (
          <div
            className="search-results"
            id="search-results"
            role="listbox"
            onScroll={() => searchRef.current?.blur()}
          >
            {shown.map((result, i) => (
              <SearchItem
                key={result.item._id}
                item={result.item}
                selected={i == selected}
                onHover={() => setSelected(i)}
                onOpen={() => open(result)}
              />
            ))}
          </div>
        ) : (
          <p className="search-hint">
            {term.trim()
              ? `Nothing found for “${term.trim()}” yet.`
              : "Titles, descriptions, saved articles and your own notes."}
          </p>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};
